import React, { useEffect, useState, useRef } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import api from "@/lib/api";
import { adminNavItems } from "./_nav";
import { useLang } from "@/contexts/LanguageContext";
import { MODULE_ICONS, STATUS_LABEL } from "@/lib/serviceModules";
import { ChevronRight, Power, Clock, X, Rocket, Square } from "lucide-react";

const STATUSES = ["live", "coming_soon", "off"];

export default function ServiceModulesAdmin() {
  const { t, lang } = useLang();
  const [modules, setModules] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);
  const [demo, setDemo] = useState({ active: false, remaining_seconds: 0 });
  const [duration, setDuration] = useState(60);
  const tickRef = useRef(null);

  const load = () => api.get("/service-modules").then(r => setModules(r.data));
  const loadDemo = () => api.get("/admin/demo-mode").then(r => setDemo(r.data));

  useEffect(() => { load(); loadDemo(); }, []);

  // Local countdown — refresh modules when demo state flips
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (demo.active && demo.remaining_seconds > 0) {
      tickRef.current = setInterval(() => {
        setDemo((d) => {
          const next = Math.max(0, d.remaining_seconds - 1);
          if (next === 0) { load(); loadDemo(); }
          return { ...d, remaining_seconds: next, active: next > 0 };
        });
      }, 1000);
    }
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [demo.active]);

  const startDemo = async () => {
    const { data } = await api.post("/admin/demo-mode/start", { duration_seconds: Number(duration) });
    setDemo(data);
    await load();
  };
  const stopDemo = async () => {
    const { data } = await api.post("/admin/demo-mode/stop");
    setDemo(data);
    await load();
  };

  const setStatus = async (key, status) => {
    if (demo.active) return; // locked during demo
    setLoadingKey(key);
    await api.patch(`/admin/service-modules/${key}`, { status });
    await load();
    setLoadingKey(null);
  };

  return (
    <DashboardLayout items={adminNavItems(t)} title={t("serviceModules")}>
      {/* LIVE DEMO MODE BANNER */}
      <div
        className={`mb-6 rounded-2xl border-2 p-5 transition-all ${
          demo.active
            ? "border-[var(--so-orange)] bg-gradient-to-r from-[var(--so-orange)]/10 to-amber-500/10"
            : "border-dashed border-[var(--so-border)]"
        }`}
        data-testid="demo-mode-banner"
      >
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${demo.active ? "bg-[var(--so-orange)] text-white" : "bg-[var(--so-orange)]/10 text-[var(--so-orange)]"}`}>
              <Rocket className={`w-6 h-6 ${demo.active ? "so-logo-spin" : ""}`} />
            </div>
            <div>
              <div className="font-bold text-lg flex items-center gap-2">
                Live Demo Mode
                {demo.active && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/15 text-red-600 border border-red-500/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 so-pulse" /> ACTIVE
                  </span>
                )}
              </div>
              <div className="text-sm text-[var(--so-text-soft)]">
                {demo.active
                  ? "Every service is temporarily live for investor pitch — auto-reverts when timer ends."
                  : "Temporarily flip every service ON. Perfect for investor pitches & sales demos."}
              </div>
            </div>
          </div>
          {demo.active ? (
            <div className="flex items-center gap-3">
              <div className="text-end">
                <div className="text-xs uppercase tracking-wider text-[var(--so-text-soft)] font-bold">Reverts in</div>
                <div className="text-3xl font-extrabold text-[var(--so-orange)] tabular-nums" data-testid="demo-countdown">
                  {String(Math.floor(demo.remaining_seconds / 60)).padStart(2, "0")}:{String(demo.remaining_seconds % 60).padStart(2, "0")}
                </div>
              </div>
              <button onClick={stopDemo} className="so-btn-secondary" data-testid="demo-stop">
                <Square className="w-4 h-4" /> End demo
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="so-input !w-auto !py-2 !px-3 text-sm font-semibold"
                data-testid="demo-duration"
              >
                <option value={60}>1 minute</option>
                <option value={180}>3 minutes</option>
                <option value={300}>5 minutes</option>
                <option value={600}>10 minutes</option>
                <option value={1800}>30 minutes</option>
              </select>
              <button onClick={startDemo} className="so-btn-primary" data-testid="demo-start">
                <Rocket className="w-4 h-4" /> Activate
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mb-6 max-w-2xl">
        <p className="text-[var(--so-text-soft)]">
          Flip any service ON, OFF, or set it as <b>Coming Soon</b>. Changes apply instantly across customer apps. <b>Restaurants</b> is live by default.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {modules.map((m) => {
          const Icon = MODULE_ICONS[m.key];
          const label = lang === "ar" ? m.label_ar : m.label_en;
          const desc = lang === "ar" ? m.description_ar : m.description_en;
          const isLive = m.status === "live";
          return (
            <div
              key={m.key}
              data-testid={`admin-module-${m.key}`}
              className="so-card p-5 transition-all"
              style={{ borderColor: isLive ? m.accent : undefined, boxShadow: isLive ? `0 0 0 1px ${m.accent}40` : undefined }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all"
                  style={{ background: `${m.accent}15`, color: m.accent }}
                >
                  <Icon className="w-7 h-7" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-lg">{label}</div>
                    <StatusBadge status={m.status} accent={m.accent} lang={lang} />
                  </div>
                  <div className="text-sm text-[var(--so-text-soft)] mt-1">{desc}</div>

                  <div className="mt-4 flex items-center gap-1 bg-[var(--so-bg)] rounded-xl p-1 w-fit">
                    {STATUSES.map((s) => {
                      const active = m.status === s;
                      const StatusIcon = s === "live" ? Power : s === "coming_soon" ? Clock : X;
                      const tint = s === "live" ? m.accent : s === "coming_soon" ? "#F59E0B" : "#94A3B8";
                      return (
                        <button
                          key={s}
                          onClick={() => setStatus(m.key, s)}
                          disabled={loadingKey === m.key || active || demo.active}
                          data-testid={`set-${m.key}-${s}`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all ${
                            active ? "text-white shadow-sm" : "text-[var(--so-text-soft)] hover:text-[var(--so-text)]"
                          }`}
                          style={active ? { background: tint } : {}}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {STATUS_LABEL[s][lang]}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-[var(--so-text-soft)] flex-shrink-0" />
              </div>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}

const StatusBadge = ({ status, accent, lang }) => {
  const dotColor = status === "live" ? accent : status === "coming_soon" ? "#F59E0B" : "#94A3B8";
  const text = STATUS_LABEL[status][lang];
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{ color: dotColor, borderColor: `${dotColor}50`, background: `${dotColor}10` }}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === "live" ? "so-pulse" : ""}`} style={{ background: dotColor }} />
      {text}
    </span>
  );
};
